/* eslint-disable no-console */
const LOG_LEVELS = {
  NONE: -1,
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
};

// To show debug messages, use:
//
// NEXT_PUBLIC_LOG_LEVEL=debug pnpm dev
//
// or:
//
// pnpm dev --debug

export function getCurrentLogLevel() {
  let logLevel: keyof typeof LOG_LEVELS = process.env.NODE_ENV === "development" ? "INFO" : "NONE";

  if (typeof window !== "undefined" && sessionStorage.getItem("corca.logLevel")) {
    const logLevelKey = sessionStorage.getItem("corca.logLevel")?.toUpperCase();
    if (logLevelKey && logLevelKey in LOG_LEVELS) {
      logLevel = logLevelKey as keyof typeof LOG_LEVELS;
    }
  }

  if (process.env.NEXT_PUBLIC_LOG_LEVEL) {
    const logLevelKey = process.env.NEXT_PUBLIC_LOG_LEVEL.toUpperCase();
    if (logLevelKey in LOG_LEVELS) {
      logLevel = logLevelKey as keyof typeof LOG_LEVELS;
    }
  }

  return logLevel;
}

const LOG_LEVEL_INDEX = LOG_LEVELS[getCurrentLogLevel()];

const noop = () => {};

function logArgs(category: string) {
  if (globalThis.process?.release.name === "node") {
    const nodejsTimer = {
      [Symbol.for("nodejs.util.inspect.custom")]: () =>
        `\x1b[90m[${performance.now().toFixed(0)}ms]\x1b[0m`,
    };

    return [nodejsTimer, `\x1b[36m[${category}]\x1b[0m`];
  }

  return [`%c[${category}]`, "color: cyan;"];
}

/**
 * Creates a logger for the specified category.
 *
 * Log level set in environment variable or via session storage controls which messages are logged.
 *
 * @example
 * const logger = createLogger("DocumentModel");
 *
 * ...
 *
 * logger.debug("This is a debug message");
 *
 *
 * To see the logs in browser console, set the log level in the browser console. For example:
 *
 * sessionStorage.setItem("corca.logLevel", "DEBUG");
 *
 * and refresh the page.
 *
 * To disable logging, set the log level to "NONE".
 *
 * sessionStorage.setItem("corca.logLevel", "NONE");
 *
 * and refresh the page.
 */
export function createLogger(category: string) {
  return {
    enabled: (level: keyof typeof LOG_LEVELS) => LOG_LEVEL_INDEX >= LOG_LEVELS[level],
    log:
      LOG_LEVEL_INDEX >= LOG_LEVELS.INFO ? console.log.bind(console, ...logArgs(category)) : noop,
    info:
      LOG_LEVEL_INDEX >= LOG_LEVELS.INFO ? console.log.bind(console, ...logArgs(category)) : noop,
    debug:
      LOG_LEVEL_INDEX >= LOG_LEVELS.DEBUG
        ? console.debug.bind(console, ...logArgs(category))
        : noop,
    warn:
      LOG_LEVEL_INDEX >= LOG_LEVELS.WARN ? console.warn.bind(console, ...logArgs(category)) : noop,
    trace:
      LOG_LEVEL_INDEX >= LOG_LEVELS.DEBUG
        ? console.trace.bind(console, ...logArgs(category))
        : noop,
    time: LOG_LEVEL_INDEX >= LOG_LEVELS.INFO ? console.time : noop,
    timeEnd: LOG_LEVEL_INDEX >= LOG_LEVELS.INFO ? console.timeEnd : noop,
  };
}
